import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../api';
import { css, Label, PageHeader, SectionHeader, Spinner, Toast, Modal, ROL_LABELS } from '../components/UI';

// Tokens para que agentes de IA consulten el sistema por MCP (solo lectura).
// Solo el admin los asigna, cada uno a un usuario: el agente ve lo mismo que
// ese usuario, nada más.

const VIGENCIAS = [
  { dias: 30, label: '30 días' },
  { dias: 90, label: '90 días' },
  { dias: 180, label: '6 meses' },
  { dias: 365, label: '1 año' },
];
const HERRAMIENTAS = [
  ['guia_del_sistema', 'Cómo funciona el sistema: estados, qué cuenta como pagado o por pagar, neto, fechas, tramos y presupuestos.'],
  ['quien_soy', 'Nombre, rol y alcance del usuario del token.'],
  ['buscar_solicitudes', 'Buscar por folio, estado, tipo, empresa, proveedor, dirección y fechas.'],
  ['obtener_solicitud', 'Detalle de un folio: pago (CLABE y cuenta enmascaradas), impuestos, conceptos, historial y comentarios.'],
  ['resumen_dashboard', 'Cifras del tablero, incluida la banda "Al día de hoy" (solo roles con tablero).'],
  ['listar_pendientes_hoy', 'Las solicitudes de un tramo de antigüedad o de la proyección de pagos.'],
  ['estado_presupuestos', 'Presupuesto contra gasto del mes (admin, finanzas y director).'],
];

const fecha = (d) => (d ? new Date(d).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const estadoDe = (t) => (t.revocado ? 'Revocado' : new Date(t.expiraEn) <= new Date() ? 'Expirado' : 'Activo');

function Codigo({ children }) {
  return (
    <pre style={{ margin: 0, padding: '12px 14px', borderRadius: 4, background: 'var(--well)', border: '1px solid var(--mesh)', color: 'var(--ink-soft)', fontFamily: 'var(--digit-font)', fontSize: 12.5, lineHeight: 1.55, overflowX: 'auto', whiteSpace: 'pre' }}>
      {children}
    </pre>
  );
}

// Conexión con OAuth: el agente (Claude) se conecta con el login de la app, sin
// token. El admin decide quién puede autorizar (backend: utils/oauthProveedor.js).
function ConexionOAuth({ avisar }) {
  const [datos, setDatos] = useState(null);
  const [buscar, setBuscar] = useState('');
  const avisarRef = useRef(avisar);
  avisarRef.current = avisar;
  const cargar = useCallback(() => api.get('/oauth/admin').then(setDatos).catch(e => avisarRef.current(e.message, 'error')), []);
  useEffect(() => { cargar(); }, [cargar]);
  const permiso = async (u, puede) => {
    try {
      const r = await api.put(`/oauth/admin/permiso/${u._id}`, { puede });
      avisar(puede ? `${u.nombre} ya puede conectar agentes` : `${u.nombre} ya no puede conectar agentes${r.desconectadas ? ` · ${r.desconectadas} conexión(es) cortada(s)` : ''}`);
      cargar();
    } catch (e) { avisar(e.message, 'error'); }
  };
  const desconectar = async (c) => {
    if (!window.confirm(`¿Desconectar ${c.cliente} de ${c.usuario?.nombre}? Tendrá que volver a autorizarse.`)) return;
    try { await api.del(`/oauth/admin/conexiones/${c._id}`); avisar('Agente desconectado'); cargar(); } catch (e) { avisar(e.message, 'error'); }
  };
  if (!datos) return <section style={{ ...css.card, padding: 20, marginBottom: 16 }}><Spinner /></section>;
  const t = buscar.trim().toLowerCase();
  const lista = t ? datos.usuarios.filter(u => `${u.nombre} ${u.email}`.toLowerCase().includes(t)) : datos.usuarios.filter(u => u.puede);
  const h2 = { margin: '0 0 4px', fontFamily: 'var(--label-font)', fontSize: 17, fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--ink)' };
  const td = { padding: '8px 10px', borderTop: '1px solid var(--mesh-soft)', fontSize: 13 };
  return (
    <section style={{ ...css.card, padding: 20, marginBottom: 16 }} aria-labelledby="conexion-oauth">
      <h2 id="conexion-oauth" style={h2}>Conectar con el login de la app (sin token)</h2>
      <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--glass)', lineHeight: 1.55 }}>
        En Claude, agrega un conector personalizado con esta dirección. Se abre el login de la app: la persona entra con su correo y su contraseña, autoriza, y el agente ve lo mismo que ella, en solo lectura. Solo pueden autorizar los usuarios con permiso; el admin siempre puede.
      </p>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 18, flexWrap: 'wrap' }}>
        <Codigo>{datos.url}</Codigo>
        <button type="button" className="nx-ghost" onClick={() => navigator.clipboard?.writeText(datos.url).then(() => avisar('Dirección copiada'))} style={{ padding: '8px 14px', fontSize: 12.5 }}>Copiar</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 360px), 1fr))', gap: 20 }}>
        <div>
          <Label>Quién puede conectar agentes</Label>
          <input value={buscar} onChange={e => setBuscar(e.target.value)} placeholder="Buscar usuario para darle permiso" style={{ ...css.input(false), marginBottom: 8 }} aria-label="Buscar usuario" />
          <div style={{ maxHeight: 260, overflowY: 'auto', border: '1px solid var(--mesh-soft)', borderRadius: 4 }}>
            {lista.length ? lista.map(u => (
              <label key={u._id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '7px 10px', borderTop: '1px solid var(--mesh-soft)', fontSize: 13, cursor: u.rol === 'admin' ? 'default' : 'pointer' }}>
                <input type="checkbox" checked={u.puede} disabled={u.rol === 'admin'} onChange={e => permiso(u, e.target.checked)} />
                <span style={{ flex: 1 }}>{u.nombre} <span style={{ color: 'var(--glass)' }}>· {ROL_LABELS[u.rol] || u.rol}{u.rol === 'admin' ? ' (siempre puede)' : ''}</span></span>
              </label>
            )) : <div style={{ padding: 10, fontSize: 12.5, color: 'var(--glass)' }}>{t ? 'Sin coincidencias.' : 'Nadie tiene permiso todavía, salvo los admin. Busca un usuario para dárselo.'}</div>}
          </div>
        </div>
        <div>
          <Label>Agentes conectados</Label>
          {datos.conexiones.length ? (
            <div style={{ overflowX: 'auto', border: '1px solid var(--mesh-soft)', borderRadius: 4 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 360 }}>
                <tbody>
                  {datos.conexiones.map(c => (
                    <tr key={c._id}>
                      <td style={td}><b>{c.cliente}</b><div style={{ fontSize: 12, color: 'var(--glass)' }}>{c.usuario?.nombre} · desde {fecha(c.desde)} · último uso {fecha(c.ultimoUso)}</div></td>
                      <td style={{ ...td, textAlign: 'right' }}><button type="button" className="nx-ghost" onClick={() => desconectar(c)} style={{ padding: '6px 12px', fontSize: 12 }}>Desconectar</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <div style={{ fontSize: 12.5, color: 'var(--glass)', padding: '8px 0' }}>Ningún agente conectado con OAuth.</div>}
        </div>
      </div>
    </section>
  );
}

export default function AgentesIA() {
  const [tokens, setTokens] = useState(null);
  const [usuarios, setUsuarios] = useState([]);
  const [usuarioId, setUsuarioId] = useState('');
  const [buscarUsuario, setBuscarUsuario] = useState('');
  const [filtro, setFiltro] = useState('');
  const [soloActivos, setSoloActivos] = useState(true);
  const [nombre, setNombre] = useState('');
  const [dias, setDias] = useState(90);
  const [creando, setCreando] = useState(false);
  const [nuevo, setNuevo] = useState(null);      // token recién creado: se muestra una sola vez
  const [copiado, setCopiado] = useState(false);
  const [cliente, setCliente] = useState('code');
  const [revocar, setRevocar] = useState(null);
  const [toast, setToast] = useState(null);

  const aviso = (msg, tipo = 'ok') => { setToast({ msg, tipo }); setTimeout(() => setToast(null), 4000); };
  const cargar = useCallback(() => api.get('/tokens-agente').then(setTokens).catch(e => { setTokens([]); aviso(e.message, 'error'); }), []);
  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => {
    api.get('/auth/usuarios?limit=2000&page=1')
      .then(r => setUsuarios((r.items || []).filter(u => u.activo !== false).sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'))))
      .catch(e => aviso(e.message, 'error'));
  }, []);

  const opcionesUsuario = usuarios.filter(u => {
    const q = buscarUsuario.trim().toLowerCase();
    return !q || u._id === usuarioId || u.nombre.toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
  });
  const usuarioSel = usuarios.find(u => u._id === usuarioId);
  const visibles = (tokens || []).filter(t => {
    if (soloActivos && estadoDe(t) !== 'Activo') return false;
    const q = filtro.trim().toLowerCase();
    return !q || [t.nombre, t.usuario?.nombre, t.usuario?.email].some(v => (v || '').toLowerCase().includes(q));
  });

  const urlMcp = `${window.location.origin}/api/mcp`;

  const crear = async (e) => {
    e.preventDefault();
    if (!nombre.trim() || !usuarioId) return;
    setCreando(true);
    try {
      const t = await api.post('/tokens-agente', { usuario: usuarioId, nombre: nombre.trim(), dias });
      setNuevo(t); setCopiado(false); setNombre('');
      cargar();
    } catch (err) { aviso(err.message, 'error'); }
    finally { setCreando(false); }
  };

  const copiar = async (valor) => {
    try { await navigator.clipboard.writeText(valor); setCopiado(true); } catch { aviso('No se pudo copiar; selecciónalo y cópialo a mano.', 'error'); }
  };

  const confirmarRevocar = async () => {
    try { await api.del(`/tokens-agente/${revocar._id}`); aviso(`Token "${revocar.nombre}" revocado`); cargar(); }
    catch (err) { aviso(err.message, 'error'); }
    finally { setRevocar(null); }
  };

  // Cada cliente pide la conexión de forma distinta: Claude Code y los que
  // aceptan encabezados van directo; Claude Desktop necesita el puente mcp-remote.
  const tk = nuevo?.token || 'spag_…';
  const CLIENTES = {
    code: {
      label: 'Claude Code',
      ayuda: 'Pega este comando en la terminal.',
      texto: `claude mcp add --transport http selectshop-pagos ${urlMcp} --header "Authorization: Bearer ${tk}"`,
    },
    desktop: {
      label: 'Claude Desktop',
      ayuda: 'Configuración → Desarrollador → Editar configuración. Pega esto en claude_desktop_config.json y reinicia Claude. Requiere Node.js instalado.',
      texto: `{
  "mcpServers": {
    "selectshop-pagos": {
      "command": "npx",
      "args": ["-y", "mcp-remote", "${urlMcp}", "--header", "Authorization:\${AUTH}"],
      "env": { "AUTH": "Bearer ${tk}" }
    }
  }
}`,
    },
    otros: {
      label: 'Grok y otros',
      ayuda: 'Para clientes que aceptan URL con encabezados (Grok, Cursor, el SDK de agentes…).',
      texto: `{
  "mcpServers": {
    "selectshop-pagos": {
      "type": "http",
      "url": "${urlMcp}",
      "headers": { "Authorization": "Bearer ${tk}" }
    }
  }
}`,
    },
  };
  const configuracion = CLIENTES[cliente].texto;

  return (
    <div>
      <Toast msg={toast?.msg} tipo={toast?.tipo} onClose={() => setToast(null)} />
      <PageHeader title="Agentes de IA" subtitle="Conecta agentes de IA al sistema por MCP, con el login de la app o con un token. Solo pueden consultar, y cada agente ve lo mismo que su usuario." />

      <ConexionOAuth avisar={aviso} />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 420px), 1fr))', gap: 16, alignItems: 'start' }}>
        {/* Crear token */}
        <section style={{ ...css.card, padding: 20 }} aria-labelledby="nuevo-token">
          <h2 id="nuevo-token" style={{ margin: '0 0 4px', fontFamily: 'var(--label-font)', fontSize: 17, fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--ink)' }}>Asignar token</h2>
          <p style={{ margin: '0 0 16px', fontSize: 13, color: 'var(--glass)', lineHeight: 1.5 }}>Uno por agente, para poder revocarlo sin afectar a los demás. El agente hereda el rol y el alcance del usuario.</p>
          <form onSubmit={crear}>
            <div style={{ marginBottom: 14 }}>
              <Label required>Usuario</Label>
              <input value={buscarUsuario} onChange={e => setBuscarUsuario(e.target.value)} placeholder="Buscar por nombre o correo" style={{ ...css.input(false), marginBottom: 6 }} aria-label="Buscar usuario" />
              <select value={usuarioId} onChange={e => setUsuarioId(e.target.value)} style={css.input(false)} aria-label="Usuario al que se asigna">
                <option value="">{opcionesUsuario.length ? 'Elige un usuario…' : 'Sin coincidencias'}</option>
                {opcionesUsuario.map(u => <option key={u._id} value={u._id}>{u.nombre} · {ROL_LABELS[u.rol] || u.rol}{u.email ? ' · ' + u.email : ''}</option>)}
              </select>
            </div>
            <div style={{ marginBottom: 14 }}>
              <Label required>Nombre</Label>
              <input value={nombre} onChange={e => setNombre(e.target.value)} maxLength={80} placeholder="Ej. Agente de tesorería" style={css.input(false)} aria-label="Nombre del token" />
            </div>
            <div style={{ marginBottom: 18 }}>
              <Label>Vigencia</Label>
              <div role="group" aria-label="Vigencia" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {VIGENCIAS.map(v => (
                  <button key={v.dias} type="button" onClick={() => setDias(v.dias)} aria-pressed={dias === v.dias}
                    className="nx-ghost" style={{ padding: '7px 12px', fontSize: 12.5, ...(dias === v.dias ? { color: 'var(--plasma)', borderColor: 'var(--plasma-line)', background: 'var(--plasma-dim)' } : {}) }}>
                    {v.label}
                  </button>
                ))}
              </div>
            </div>
            <button type="submit" className="nx-plate" disabled={creando || !nombre.trim() || !usuarioId} style={{ opacity: creando || !nombre.trim() || !usuarioId ? 0.55 : 1 }}>
              {creando ? 'Asignando…' : usuarioSel ? `Asignar a ${usuarioSel.nombre.split(' ')[0]}` : 'Asignar token'}
            </button>
          </form>
        </section>

        {/* Cómo conectar */}
        <section style={{ ...css.card, padding: 20 }} aria-labelledby="conectar">
          <h2 id="conectar" style={{ margin: '0 0 4px', fontFamily: 'var(--label-font)', fontSize: 17, fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--ink)' }}>Cómo conectar</h2>
          <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--glass)', lineHeight: 1.5 }}>
            Servidor MCP por HTTP (Streamable HTTP) con token en el encabezado. Para agentes propios, scripts o clientes sin OAuth (Grok, Cursor, el SDK de agentes). Para claude.ai y Claude Code es más fácil la conexión con el login de la app, arriba.
          </p>
          <Label>URL del servidor</Label>
          <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
            <input readOnly value={urlMcp} style={{ ...css.input(false), fontFamily: 'var(--digit-font)' }} aria-label="URL del servidor MCP" onFocus={e => e.target.select()} />
            <button type="button" className="nx-ghost" onClick={() => copiar(urlMcp)} style={{ padding: '0 12px', fontSize: 12.5, flexShrink: 0 }}>Copiar</button>
          </div>
          <Label>Encabezado</Label>
          <Codigo>Authorization: Bearer spag_…</Codigo>
          <div style={{ marginTop: 14 }}>
            <Label>Herramientas (todas de solo lectura)</Label>
            <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: 6 }}>
              {HERRAMIENTAS.map(([n, d]) => (
                <li key={n} style={{ fontSize: 12.5, color: 'var(--ink-soft)', lineHeight: 1.45 }}>
                  <code style={{ fontFamily: 'var(--digit-font)', color: 'var(--plasma)' }}>{n}</code> · {d}
                </li>
              ))}
            </ul>
          </div>
        </section>
      </div>

      <SectionHeader>Tokens asignados</SectionHeader>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
        <input value={filtro} onChange={e => setFiltro(e.target.value)} placeholder="Filtrar por usuario o nombre del token" style={{ ...css.input(false), maxWidth: 340 }} aria-label="Filtrar tokens" />
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--ink-soft)', cursor: 'pointer' }}>
          <input type="checkbox" checked={soloActivos} onChange={e => setSoloActivos(e.target.checked)} /> Solo activos
        </label>
        {tokens && <span style={{ fontSize: 12.5, color: 'var(--glass)', marginLeft: 'auto' }}>{visibles.length} de {tokens.length}</span>}
      </div>
      {!tokens ? <Spinner /> : visibles.length === 0 ? (
        <div style={{ ...css.card, padding: 24, textAlign: 'center', fontSize: 13, color: 'var(--glass)' }}>{tokens.length === 0 ? 'Aún no hay tokens. Asigna uno arriba para conectar el primer agente.' : 'Ningún token coincide con el filtro.'}</div>
      ) : (
        <div style={{ ...css.card, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr>{['Usuario', 'Nombre', 'Token', 'Creado', 'Expira', 'Último uso', 'Estado', ''].map(h => <th key={h} scope="col" style={{ padding: '9px 14px', textAlign: 'left', whiteSpace: 'nowrap' }}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {visibles.map(t => {
                const estado = estadoDe(t);
                return (
                  <tr key={t._id} style={{ opacity: estado === 'Activo' ? 1 : 0.6 }}>
                    <td style={{ padding: '10px 14px' }}>
                      <div style={{ color: 'var(--ink)', fontWeight: 600 }}>{t.usuario?.nombre || 'Usuario eliminado'}</div>
                      <div style={{ fontSize: 11.5, color: 'var(--glass)' }}>{t.usuario ? (ROL_LABELS[t.usuario.rol] || t.usuario.rol) + (t.usuario.activo === false ? ' · inactivo' : '') : ''}</div>
                    </td>
                    <td style={{ padding: '10px 14px', color: 'var(--ink-soft)' }}>{t.nombre}</td>
                    <td style={{ padding: '10px 14px', fontFamily: 'var(--digit-font)', color: 'var(--glass)', whiteSpace: 'nowrap' }}>{t.prefijo}…</td>
                    <td style={{ padding: '10px 14px', fontFamily: 'var(--digit-font)', color: 'var(--glass)', whiteSpace: 'nowrap' }}>{fecha(t.createdAt)}</td>
                    <td style={{ padding: '10px 14px', fontFamily: 'var(--digit-font)', color: 'var(--glass)', whiteSpace: 'nowrap' }}>{fecha(t.expiraEn)}</td>
                    <td style={{ padding: '10px 14px', fontFamily: 'var(--digit-font)', color: 'var(--glass)', whiteSpace: 'nowrap' }}>{t.ultimoUso ? fecha(t.ultimoUso) : 'Nunca'}</td>
                    <td style={{ padding: '10px 14px', whiteSpace: 'nowrap', color: estado === 'Activo' ? 'var(--ok)' : 'var(--glass)', fontWeight: 600 }}>{estado}</td>
                    <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                      {estado === 'Activo' && (
                        <button type="button" className="nx-ghost" onClick={() => setRevocar(t)} style={{ padding: '5px 12px', fontSize: 12.5, color: 'var(--danger)', borderColor: 'var(--danger-line)' }}>Revocar</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Token recién creado: única vez que se ve completo */}
      <Modal open={!!nuevo} onClose={() => setNuevo(null)} title="Token creado" width={620}>
        <p role="alert" style={{ margin: '0 0 14px', padding: '10px 12px', borderRadius: 4, background: 'rgba(245,207,74,0.08)', border: '1px solid rgba(245,207,74,0.35)', color: 'var(--st-pendiente)', fontSize: 13, lineHeight: 1.5 }}>
          Cópialo ahora: por seguridad no se vuelve a mostrar. Entrégalo a {nuevo?.usuario?.nombre || 'el usuario'} por un canal privado; quien lo tenga puede consultar el sistema como esa persona.
        </p>
        <Label>Token "{nuevo?.nombre}" de {nuevo?.usuario?.nombre}</Label>
        <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
          <input readOnly value={nuevo?.token || ''} style={{ ...css.input(false), fontFamily: 'var(--digit-font)' }} aria-label="Token" onFocus={e => e.target.select()} />
          <button type="button" className="nx-plate" onClick={() => copiar(nuevo.token)} style={{ width: 'auto', padding: '0 16px', flexShrink: 0 }}>{copiado ? 'Copiado' : 'Copiar'}</button>
        </div>
        <Label>Configuración para el cliente MCP</Label>
        <div role="group" aria-label="Cliente MCP" style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
          {Object.entries(CLIENTES).map(([k, c]) => (
            <button key={k} type="button" onClick={() => setCliente(k)} aria-pressed={cliente === k}
              className="nx-ghost" style={{ padding: '6px 12px', fontSize: 12.5, ...(cliente === k ? { color: 'var(--plasma)', borderColor: 'var(--plasma-line)', background: 'var(--plasma-dim)' } : {}) }}>
              {c.label}
            </button>
          ))}
        </div>
        <p style={{ margin: '0 0 8px', fontSize: 12.5, color: 'var(--glass)', lineHeight: 1.5 }}>{CLIENTES[cliente].ayuda}</p>
        <Codigo>{configuracion}</Codigo>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button type="button" className="nx-ghost" onClick={() => copiar(configuracion)} style={{ padding: '8px 14px', fontSize: 12.5 }}>Copiar configuración</button>
          <button type="button" className="nx-plate" onClick={() => setNuevo(null)} style={{ width: 'auto', padding: '8px 18px' }}>Listo</button>
        </div>
      </Modal>

      <Modal open={!!revocar} onClose={() => setRevocar(null)} title="Revocar token" width={440}>
        <p style={{ margin: '0 0 18px', fontSize: 13.5, color: 'var(--ink-soft)', lineHeight: 1.55 }}>
          El agente que use <b style={{ color: 'var(--ink)' }}>"{revocar?.nombre}"</b> de {revocar?.usuario?.nombre} dejará de poder conectarse de inmediato. No se puede deshacer.
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" className="nx-ghost" onClick={() => setRevocar(null)} style={{ padding: '8px 14px', fontSize: 12.5 }}>Cancelar</button>
          <button type="button" onClick={confirmarRevocar} style={{ ...css.btn('#be123c'), color: '#ffffff' }}>Revocar</button>
        </div>
      </Modal>
    </div>
  );
}
